import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Share2, Trash2, Check, Minus } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCalculatorName } from '../config/features';

const Log = forwardRef(({ log = [], onDelete, onShare, isMobile, previousCalculator }, ref) => {
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [sharedIndices, setSharedIndices] = useState({});
  const logContainerRef = useRef(null);
  const isFullPage = window.location.pathname === '/logs';
  const navigate = useNavigate();
  const location = useLocation();

  const getPreviousCalculatorName = () => {
    if (location.state?.calculatorName) return location.state.calculatorName;
    if (location.state?.from) return getCalculatorName(location.state.from);
    return getCalculatorName(previousCalculator) || 'Calculator';
  };

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      if (logContainerRef.current) logContainerRef.current.scrollTop = 0;
    }
  }));

  const handleShareLog = (entry, index) => {
    const shareUrl = window.location.href;
    const title = 'Calculator Log Entry';
    
    if (isMobile && navigator.share) {
      navigator.share({ title, text: entry, url: shareUrl })
        .then(() => {
          setSharedIndices(prev => ({ ...prev, [index]: true }));
          setTimeout(() => setSharedIndices(prev => ({ ...prev, [index]: false })), 500);
        })
        .catch(err => console.error('Error sharing:', err));
    } else {
      navigator.clipboard.writeText(entry)
        .then(() => {
          setSharedIndices(prev => ({ ...prev, [index]: true }));
          setTimeout(() => setSharedIndices(prev => ({ ...prev, [index]: false })), 500);
        })
        .catch(err => console.error('Error copying to clipboard:', err));
    }
  };

  const handleBack = () => {
    if (location.state?.from) navigate(location.state.from);
    else navigate(-1);
  };

  if (!log || log.length === 0) {
    return (
      <div ref={logContainerRef} className={`mt-0 overflow-y-auto ${
        isFullPage ? 'h-[calc(100vh-200px)]' : 'h-[calc(100vh-50px)]'
      } scrollbar-hide`}>
        <div className="bg-surface-card rounded-lg border border-border p-3 mb-2 group">
          <div className="flex justify-between items-center">
            <span className="text-sm text-ink-tertiary flex-grow pr-4">No entries yet</span>
            <div className="flex space-x-1.5">
              <button disabled aria-label="Share log entry" className="p-2 text-xs bg-semantic-gray-100 text-ink-tertiary rounded-lg">
                <Share2 className="w-3.5 h-3.5" />
              </button>
              <button disabled aria-label="Delete log entry" className="p-2 text-xs bg-semantic-gray-100 text-ink-tertiary rounded-lg">
                <Minus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={logContainerRef} 
      className={`overflow-y-auto ${
        isFullPage ? 'h-[calc(100vh-200px)]' : 'h-[calc(100vh-50px)]'
      } scrollbar-hide`}
    >
      {log.map((entry, index) => (
        <div key={index} className="bg-surface-card p-3 mb-1.5 group rounded-lg border border-border text-left transition-colors hover:border-semantic-gray-300">
          <div className="flex justify-between items-center">
            <p className="text-sm text-ink flex-grow pr-4 whitespace-pre-line">{entry}</p>
            <div className="flex space-x-1.5 flex-shrink-0">
              <button
                onClick={() => handleShareLog(entry, index)}
                aria-label="Share log entry"
                className={`p-2 text-xs rounded-lg transition-colors ${
                  sharedIndices[index]
                    ? 'bg-conform-yellow/10 text-conform-yellow'
                    : 'bg-conform-red/5 text-conform-red hover:bg-conform-red/10'
                }`}
              >
                {sharedIndices[index] ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => onDelete(index)}
                aria-label="Delete log entry"
                className="p-2 text-xs bg-red-900/20 text-red-400 rounded-lg hover:bg-red-900/30 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
});

Log.displayName = 'Log';

export default Log;
